import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export type TablePreferences = {
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  page_size?: number;
  current_page?: number;
  filters?: Record<string, any>;
};

export type UiPreferences = {
  theme?: 'light' | 'dark';
  collector_dashboard?: {
    show_plan_limits?: boolean;
    show_kpis?: boolean;
    show_basic_stats?: boolean;
    show_next_feedings?: boolean;
    show_recent_health?: boolean;
    show_growth_trends?: boolean;
    show_state_legality?: boolean;
  };
  facility_dashboard?: {
    show_security_alerts?: boolean;
    show_key_metrics?: boolean;
    show_compliance_overview?: boolean;
    show_animal_health?: boolean;
    show_top_enclosures?: boolean;
    show_recent_activity?: boolean;
  };
  nav_state?: {
    facility_open?: boolean;
    collector_open?: boolean;
    general_open?: boolean;
    admin_open?: boolean;
  };
  table_preferences?: {
    animals?: TablePreferences;
    my_collection?: TablePreferences;
    permits?: TablePreferences;
  };
};

const DEFAULT_PREFERENCES: UiPreferences = {
  theme: 'light',
  collector_dashboard: {
    show_plan_limits: true,
    show_kpis: true,
    show_basic_stats: true,
    show_next_feedings: true,
    show_recent_health: true,
    show_growth_trends: true,
    show_state_legality: true,
  },
  facility_dashboard: {
    show_security_alerts: true,
    show_key_metrics: true,
    show_compliance_overview: true,
    show_animal_health: true,
    show_top_enclosures: true,
    show_recent_activity: true,
  },
  nav_state: {
    facility_open: true,
    collector_open: true,
    general_open: true,
    admin_open: true,
  },
  table_preferences: {
    animals: {
      sort_by: 'common_name',
      sort_dir: 'asc',
      page_size: 25,
      filters: {},
    },
    my_collection: {
      sort_by: 'species',
      sort_dir: 'asc',
      page_size: 25,
      filters: {},
    },
    permits: {
      sort_by: 'expiry_date',
      sort_dir: 'asc',
      page_size: 25,
      filters: { status: 'Active' },
    },
  },
};

// Deep merge helper
function deepMerge(target: any, source: any): any {
  const output = { ...target };
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          Object.assign(output, { [key]: source[key] });
        } else {
          output[key] = deepMerge(target[key], source[key]);
        }
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }
  return output;
}

function isObject(item: any): boolean {
  return item && typeof item === 'object' && !Array.isArray(item);
}

export const useUiPreferences = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: prefs, isLoading, error } = useQuery({
    queryKey: ['ui-preferences', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        // For logged-out users, check localStorage first, then system preference
        const storedTheme = localStorage.getItem('zoocertica_theme') as 'light' | 'dark' | null;
        if (storedTheme) {
          return { ...DEFAULT_PREFERENCES, theme: storedTheme };
        }
        
        // Check system preference
        const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        return { ...DEFAULT_PREFERENCES, theme: systemTheme };
      }

      const { data, error } = await supabase.functions.invoke('get-ui-preferences', {
        headers: {
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
      });

      if (error) {
        console.error('Failed to fetch UI preferences:', error);
        // Fallback: check localStorage, then system, then defaults
        const storedTheme = localStorage.getItem('zoocertica_theme') as 'light' | 'dark' | null;
        if (storedTheme) {
          return { ...DEFAULT_PREFERENCES, theme: storedTheme };
        }
        const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        return { ...DEFAULT_PREFERENCES, theme: systemTheme };
      }

      // Merge fetched prefs with defaults to ensure all keys exist
      const merged = deepMerge(DEFAULT_PREFERENCES, data || {});
      
      // If no theme in server data, check localStorage, then system
      if (!merged.theme) {
        const storedTheme = localStorage.getItem('zoocertica_theme') as 'light' | 'dark' | null;
        if (storedTheme) {
          merged.theme = storedTheme;
        } else {
          merged.theme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
      }
      
      return merged;
    },
    enabled: !!user?.id,
    staleTime: 30000, // Cache for 30 seconds
    retry: 1, // Only retry once
  });

  const updatePrefsMutation = useMutation({
    mutationFn: async (patch: Partial<UiPreferences>) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase.functions.invoke('update-ui-preferences', {
        body: patch,
        headers: {
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
      });

      if (error) throw error;
      
      // Fire telemetry event for each changed preference
      Object.entries(patch).forEach(([areaKey, areaPrefs]) => {
        if (typeof areaPrefs === 'object' && areaPrefs !== null) {
          Object.entries(areaPrefs).forEach(([prefKey, newValue]) => {
            // Log analytics event (fire and forget - don't block on this)
            console.log(`[Telemetry] UI pref changed: ${areaKey}.${prefKey} = ${newValue}`);
          });
        }
      });
      
      return data;
    },
    onSuccess: (updatedPrefs) => {
      // Update cache with merged preferences
      queryClient.setQueryData(['ui-preferences', user?.id], updatedPrefs);
      toast.success('Dashboard preferences updated');
    },
    onError: (error) => {
      console.error('Failed to update UI preferences:', error);
      toast.error('Failed to update preferences. Please try again.');
    },
  });

  return {
    prefs: prefs || DEFAULT_PREFERENCES,
    loading: isLoading,
    error,
    updatePrefs: updatePrefsMutation.mutate,
    isUpdating: updatePrefsMutation.isPending,
  };
};
